/**
 * Plan Audit — the Tier 1 checks, worded for a person. Pure.
 *
 * PRD: docs/9-exploration/PRD-plan-audit.md.
 *
 * This module detects nothing on its own. The duplicate-cluster and
 * unlike-things findings arrive from `computeViolations` (poolViolations.ts),
 * closed duplicates from `isClosedDuplicate` (clusterResolution.ts), and
 * objective links from `generateAlignmentReport` (strategyAlignment.ts). What
 * lives here is the grouping, the sentence, and the link to the fix.
 *
 * Two checks hold the plan against the Cascade it came from (roadmap D-9):
 * whether an accepted Catchball proposal carries each commitment, and whether
 * what it serves is what the cycle said it set out to do. Both are set
 * membership on ids the service read — no scoring, no AI.
 *
 * It informs; it never blocks. Nothing here is consulted by authorization.
 */

import { isClosedDuplicate } from './clusterResolution';
import type { PoolViolation } from './poolViolations';

export type PlanAuditCheck = 'duplicates' | 'orphans' | 'reasons' | 'unlike' | 'cascade' | 'intent';

export const PLAN_AUDIT_CHECK_LABELS: Record<PlanAuditCheck, string> = {
  duplicates: 'Duplicates',
  orphans: 'No owner or no objective',
  reasons: 'No reason given',
  unlike: 'Unlike things ranked together',
  cascade: 'Not accepted in the Cascade',
  intent: 'Outside the cycle’s stated intent',
};

/** The order the panel shows the checks in, and counts them in. */
export const PLAN_AUDIT_CHECK_ORDER: PlanAuditCheck[] = ['duplicates', 'orphans', 'reasons', 'unlike', 'cascade', 'intent'];

/** A platform item on the plan, narrowed to what the checks read. */
export interface PlanAuditItem {
  id: string;
  title: string;
  /** Above the version's line — what authorizing it commits to. */
  aboveLine: boolean;
  ownerId: string | null;
  whyNow: string | null;
  desiredOutcome: string | null;
  dedupeStatus: string | null;
  duplicateOfId: string | null;
  exitStatus: string | null;
  itemCategory: string | null;
  altitudeLevel: number | null;
  /**
   * The strategic objectives the item is linked to (objective_priority_mappings).
   * A goal link counts through the objective it sits under (migration 924).
   */
  objectiveIds: readonly string[];
}

/** An accepted Catchball proposal of the plan's cycle, narrowed to what it names. */
export interface PlanAuditProposal {
  id: string;
  /** An item-level proposal names one item. */
  itemId: string | null;
  /** A packet names its ranked items in order (aggregated_rank_list). */
  rankList: readonly string[];
  /** Ranks 1..N are above the packet's line. Null or 0: no line drawn, so the whole list is carried. */
  cutLine: number | null;
  /** The strategic objectives the proposal says it responds to. */
  objectiveIds: readonly string[];
}

/** The prioritization cycle the plan was built from: its Cascade and its stated intent. */
export interface PlanAuditCycle {
  id: string;
  name: string;
  /** Catchball loops the cycle ran. */
  loopCount: number;
  /** Its proposals in an accepted state (accepted, accepted provisionally). */
  acceptedProposals: readonly PlanAuditProposal[];
  /** The classic strategy the cycle supports, or null when it names none the audit can read. */
  strategy: { id: string; name: string } | null;
  /** Objectives under that strategy: the ids an item's objectives are compared with. */
  intentObjectiveIds: ReadonlySet<string>;
  /** Another kind of strategy the cycle names (a V2MOM, a kernel…), which carries no strategic objectives. */
  otherStrategy: { kindLabel: string; name: string } | null;
  /** The cycle's intent in words (a strategy typed by name, a north-star goal, a request's strategic context). */
  intentWords: readonly string[];
  /** Every objective's name, to say which ones an item names. */
  objectiveNames: ReadonlyMap<string, string>;
}

export interface PlanAuditInput {
  items: readonly PlanAuditItem[];
  /** Entries on the plan with no platform item behind them; cannot be checked. */
  uncheckableCount: number;
  /** Findings of the existing relationship detection over the plan's items. */
  violations: readonly PoolViolation[];
  /**
   * Items with no link to a strategic objective, or null when the workspace
   * has no objectives at all (nothing to link to — the check does not run).
   */
  unlinkedItemIds: ReadonlySet<string> | null;
  /**
   * The cycle the plan was built from, or null when there is none to read; the
   * two Cascade checks then do not run, and `cycleUnreadReason` says why.
   */
  cycle: PlanAuditCycle | null;
  cycleUnreadReason: string | null;
}

export interface PlanAuditLink {
  label: string;
  href: string;
}

export interface PlanAuditFinding {
  id: string;
  check: PlanAuditCheck;
  sentence: string;
  /** Where each finding is fixed. Item links first, then the page for the act. */
  links: PlanAuditLink[];
  /** Every item the finding names, counted in the summary. */
  itemIds: string[];
}

export interface PlanAuditResult {
  findings: PlanAuditFinding[];
  checkedItems: number;
  uncheckableCount: number;
  /** Plain lines for checks that could not run, so silence is never read as a pass. */
  notRun: string[];
  /** Per check, how many items it named; null when it could not run. */
  counts: Record<PlanAuditCheck, number | null>;
  /** What the Cascade checks read, one line each, so a clean result can be trusted. */
  readFrom: string[];
}

export const itemHref = (id: string) => `/prioritization/items/${id}`;
/** "Strategy it supports" on the item page lives on its Connections tab. */
export const itemStrategyHref = (id: string) => `/prioritization/items/${id}?tab=connections`;
/** The cycle's Cascade: every loop drawn as the path it runs over. */
export const cycleCascadeHref = (cycleId: string) => `/prioritization/cascade/${cycleId}`;
const CLUSTERS_HREF = '/prioritization/relationships/clusters';
const ALIGNMENT_HREF = '/prioritization/strategy/alignment';

/** How many items to name before the sentence says "and N more". */
const NAMED_LIMIT = 5;

function quoted(titles: readonly string[]): string {
  const named = titles.slice(0, NAMED_LIMIT).map((t) => `"${t}"`);
  const rest = titles.length - named.length;
  const list = named.length <= 1 ? named.join('') : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
  return rest > 0 ? `${named.join(', ')} and ${rest} more` : list;
}

function itemLinks(items: readonly PlanAuditItem[], href: (id: string) => string = itemHref): PlanAuditLink[] {
  return items.slice(0, NAMED_LIMIT).map((i) => ({ label: i.title, href: href(i.id) }));
}

const ids = (items: readonly PlanAuditItem[]) => items.map((i) => i.id);

/** "1 item above the line is" / "3 items above the line are". */
function itemsAbove(n: number, singular: string, plural: string): string {
  return n === 1 ? `1 item above the line ${singular}` : `${n} items above the line ${plural}`;
}

function counted(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Which items the cycle's accepted proposals carry — named by an item-level
 * proposal, or above a packet's cut line — and which they name only below it.
 * Each carried item keeps the objectives its proposals say they respond to.
 */
function readCascade(proposals: readonly PlanAuditProposal[]): {
  carried: Map<string, Set<string>>;
  belowLine: Set<string>;
} {
  const carried = new Map<string, Set<string>>();
  const belowLine = new Set<string>();
  const carry = (itemId: string, p: PlanAuditProposal) => {
    const objectives = carried.get(itemId) ?? new Set<string>();
    p.objectiveIds.forEach((o) => objectives.add(o));
    carried.set(itemId, objectives);
  };
  for (const p of proposals) {
    if (p.itemId) carry(p.itemId, p);
    const line = p.cutLine != null && p.cutLine > 0 ? p.cutLine : null;
    p.rankList.forEach((itemId, index) => {
      if (line == null || index < line) carry(itemId, p);
      else belowLine.add(itemId);
    });
  }
  return { carried, belowLine };
}

export function runPlanAudit(input: PlanAuditInput): PlanAuditResult {
  const byId = new Map(input.items.map((i) => [i.id, i]));
  const findings: PlanAuditFinding[] = [];
  const notRun: string[] = [];
  const readFrom: string[] = [];
  const skipped = new Set<PlanAuditCheck>();

  // ── Duplicates ────────────────────────────────────────────────────────────
  const closed = input.items.filter((i) =>
    isClosedDuplicate({ id: i.id, dedupeStatus: i.dedupeStatus, duplicateOfId: i.duplicateOfId, exit_status: i.exitStatus }),
  );
  for (const item of closed) {
    findings.push({
      id: `closed-duplicate:${item.id}`,
      check: 'duplicates',
      sentence: `"${item.title}" was closed as a duplicate of another item, but it is still on this plan.`,
      links: [
        { label: item.title, href: itemHref(item.id) },
        ...(item.duplicateOfId ? [{ label: 'The item it duplicates', href: itemHref(item.duplicateOfId) }] : []),
      ],
      itemIds: [item.id],
    });
  }
  for (const v of input.violations.filter((x) => x.patternId === 'A1_two_tickets_one_bet')) {
    const members = v.itemIds.map((id) => byId.get(id)).filter((i): i is PlanAuditItem => Boolean(i));
    if (members.length < 2) continue;
    findings.push({
      id: `cluster:${[...v.itemIds].sort().join(',')}`,
      check: 'duplicates',
      sentence: `${quoted(members.map((m) => m.title))} may be the same thing. Nobody has settled whether they are duplicates.`,
      links: [...itemLinks(members), { label: 'Settle it on the clusters page', href: CLUSTERS_HREF }],
      itemIds: ids(members),
    });
  }

  // ── Orphans ───────────────────────────────────────────────────────────────
  const ownerless = input.items.filter((i) => !i.ownerId);
  if (ownerless.length > 0) {
    findings.push({
      id: 'no-owner',
      check: 'orphans',
      sentence: `${ownerless.length === 1 ? '1 item has' : `${ownerless.length} items have`} no owner: ${quoted(ownerless.map((i) => i.title))}.`,
      links: itemLinks(ownerless),
      itemIds: ids(ownerless),
    });
  }
  if (input.unlinkedItemIds === null) {
    notRun.push('Objective links were not checked: this workspace has no strategic objectives to link to.');
  } else {
    const unlinked = input.items.filter((i) => input.unlinkedItemIds!.has(i.id));
    if (unlinked.length > 0) {
      findings.push({
        id: 'no-objective',
        check: 'orphans',
        sentence: `${unlinked.length === 1 ? '1 item is' : `${unlinked.length} items are`} not linked to any strategic objective: ${quoted(unlinked.map((i) => i.title))}.`,
        links: [...itemLinks(unlinked), { label: 'Link them on Strategy → Alignment', href: ALIGNMENT_HREF }],
        itemIds: ids(unlinked),
      });
    }
  }

  // ── Missing reason (above the line only) ─────────────────────────────────
  const reasonless = input.items.filter(
    (i) => i.aboveLine && !(i.whyNow ?? '').trim() && !(i.desiredOutcome ?? '').trim(),
  );
  if (reasonless.length > 0) {
    findings.push({
      id: 'no-reason',
      check: 'reasons',
      sentence: `${reasonless.length === 1 ? '1 item above the line says' : `${reasonless.length} items above the line say`} neither why now nor what it should achieve: ${quoted(reasonless.map((i) => i.title))}.`,
      links: itemLinks(reasonless),
      itemIds: ids(reasonless),
    });
  }

  // ── Unlike things ranked together ────────────────────────────────────────
  for (const v of input.violations.filter((x) => x.patternId === 'B1_altitude_salad')) {
    const kinds = new Set(input.items.map((i) => `${i.itemCategory ?? '?'}|${i.altitudeLevel ?? '?'}`));
    const offenders = v.itemIds.map((id) => byId.get(id)).filter((i): i is PlanAuditItem => Boolean(i));
    if (offenders.length === 0) continue;
    findings.push({
      id: 'unlike',
      check: 'unlike',
      sentence: `This plan ranks ${kinds.size} different kinds of item (category and level) against each other. The fewest-in-number kinds are ${quoted(offenders.map((i) => i.title))}. Ranking unlike things is allowed; share of capacity, not rank, is how they are meant to be balanced.`,
      links: itemLinks(offenders),
      itemIds: ids(offenders),
    });
  }

  // ── The Cascade behind the plan, and the intent it was run for ───────────
  const cycle = input.cycle;
  const aboveLine = input.items.filter((i) => i.aboveLine);
  if (!cycle) {
    skipped.add('cascade');
    skipped.add('intent');
    if (input.cycleUnreadReason) notRun.push(input.cycleUnreadReason);
  } else {
    const accepted = cycle.acceptedProposals;
    const { carried, belowLine } = readCascade(accepted);
    const cascadeLink = { label: 'Open the Cascade for this cycle', href: cycleCascadeHref(cycle.id) };

    // A commitment with no accepted proposal behind it.
    if (cycle.loopCount === 0 && accepted.length === 0) {
      skipped.add('cascade');
      notRun.push(
        `"${cycle.name}" ran no Cascade, so nothing on this plan was accepted through one. Items were not checked one by one for an accepted proposal.`,
      );
    } else {
      readFrom.push(
        `${PLAN_AUDIT_CHECK_LABELS.cascade}: read from ${counted(accepted.length, 'accepted proposal', 'accepted proposals')} across ${counted(cycle.loopCount, 'loop', 'loops')} of "${cycle.name}".`,
      );
      const uncarried = aboveLine.filter((i) => !carried.has(i.id));
      const onlyBelow = uncarried.filter((i) => belowLine.has(i.id));
      const nowhere = uncarried.filter((i) => !belowLine.has(i.id));
      if (nowhere.length > 0) {
        findings.push({
          id: 'no-cascade',
          check: 'cascade',
          sentence:
            accepted.length === 0
              ? `Nothing in this cycle's Cascade has been accepted yet, so ${itemsAbove(nowhere.length, 'has', 'have')} no accepted proposal behind ${nowhere.length === 1 ? 'it' : 'them'}: ${quoted(nowhere.map((i) => i.title))}.`
              : `${itemsAbove(nowhere.length, 'is', 'are')} in no accepted Catchball proposal: ${quoted(nowhere.map((i) => i.title))}.`,
          links: [...itemLinks(nowhere), cascadeLink],
          itemIds: ids(nowhere),
        });
      }
      if (onlyBelow.length > 0) {
        findings.push({
          id: 'below-cascade-line',
          check: 'cascade',
          sentence: `${itemsAbove(onlyBelow.length, 'was', 'were')} accepted in the Cascade only below the cut line: ${quoted(onlyBelow.map((i) => i.title))}.`,
          links: [...itemLinks(onlyBelow), cascadeLink],
          itemIds: ids(onlyBelow),
        });
      }
    }

    // What the commitment serves, against what the cycle set out to do.
    const intent = cycle.intentObjectiveIds;
    if (!cycle.strategy) {
      skipped.add('intent');
      notRun.push(
        cycle.otherStrategy
          ? `"${cycle.name}" supports the ${cycle.otherStrategy.kindLabel} "${cycle.otherStrategy.name}", whose intent is not stated as strategic objectives, so items above the line were not compared with it.`
          : cycle.intentWords.length > 0
            ? `"${cycle.name}" states its intent only in words ("${cycle.intentWords[0]}"), not as strategic objectives, so items above the line were not compared with it.`
            : `"${cycle.name}" states no intent to compare against: it is linked to no strategy.`,
      );
    } else if (intent.size === 0) {
      skipped.add('intent');
      notRun.push(
        `"${cycle.name}" supports the strategy "${cycle.strategy.name}", which has no objectives yet, so items above the line could not be compared with it.`,
      );
    } else {
      const naming = accepted.filter((p) => p.objectiveIds.length > 0).length;
      readFrom.push(
        `${PLAN_AUDIT_CHECK_LABELS.intent}: read from the strategy "${cycle.strategy.name}" (${counted(intent.size, 'objective', 'objectives')}), the objective and goal links of ${counted(aboveLine.length, 'item', 'items')} above the line, and ${counted(naming, 'accepted proposal that names objectives', 'accepted proposals that name objectives')}.`,
      );
      const named = (i: PlanAuditItem) => [...new Set([...i.objectiveIds, ...(carried.get(i.id) ?? [])])];
      const nameOf = (id: string) => cycle.objectiveNames.get(id) ?? 'an objective this workspace no longer has';
      const outsideNames = (list: readonly PlanAuditItem[]) =>
        quoted([...new Set(list.flatMap((i) => named(i).filter((o) => !intent.has(o))))].map(nameOf));

      const unnamed = aboveLine.filter((i) => named(i).length === 0);
      const offIntent = aboveLine.filter((i) => named(i).length > 0 && named(i).every((o) => !intent.has(o)));
      const partly = aboveLine.filter((i) => named(i).some((o) => intent.has(o)) && named(i).some((o) => !intent.has(o)));
      const strategy = `"${cycle.strategy.name}"`;

      if (offIntent.length > 0) {
        findings.push({
          id: 'off-intent',
          check: 'intent',
          sentence: `${itemsAbove(offIntent.length, 'supports', 'support')} only objectives outside ${strategy}, the strategy this cycle supports: ${quoted(offIntent.map((i) => i.title))}. ${offIntent.length === 1 ? 'It names' : 'They name'} ${outsideNames(offIntent)}.`,
          links: itemLinks(offIntent, itemStrategyHref),
          itemIds: ids(offIntent),
        });
      }
      if (unnamed.length > 0) {
        findings.push({
          id: 'no-intent-objective',
          check: 'intent',
          sentence: `${itemsAbove(unnamed.length, 'names', 'name')} no objective, on the item or in an accepted proposal, so nothing ties ${unnamed.length === 1 ? 'it' : 'them'} to ${strategy}: ${quoted(unnamed.map((i) => i.title))}.`,
          links: itemLinks(unnamed, itemStrategyHref),
          itemIds: ids(unnamed),
        });
      }
      if (partly.length > 0) {
        findings.push({
          id: 'partly-off-intent',
          check: 'intent',
          sentence: `${itemsAbove(partly.length, 'serves', 'serve')} ${strategy} and also objectives outside it: ${quoted(partly.map((i) => i.title))}. Outside it: ${outsideNames(partly)}.`,
          links: itemLinks(partly, itemStrategyHref),
          itemIds: ids(partly),
        });
      }
    }
  }

  if (input.uncheckableCount > 0) {
    notRun.push(
      `${input.uncheckableCount === 1 ? '1 entry on this plan is' : `${input.uncheckableCount} entries on this plan are`} not linked to an item, so ${input.uncheckableCount === 1 ? 'it' : 'they'} could not be checked.`,
    );
  }

  const counts = Object.fromEntries(
    PLAN_AUDIT_CHECK_ORDER.map((check) => [
      check,
      skipped.has(check) ? null : new Set(findings.filter((f) => f.check === check).flatMap((f) => f.itemIds)).size,
    ]),
  ) as Record<PlanAuditCheck, number | null>;

  return { findings, checkedItems: input.items.length, uncheckableCount: input.uncheckableCount, notRun, counts, readFrom };
}
